import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { AddCircleOutlineOutlined, Close } from "@mui/icons-material";
import RichTextEditor from "../../../components/common/RichTextEditor";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

interface NotesCardProps {
  streamId?: number;
}

interface StreamResponse {
  stream_evaluation_summary: string | null;
}

const NotesCard: React.FC<NotesCardProps> = ({ streamId }) => {
  const [open, setOpen] = useState(false);
  const [notesContent, setNotesContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        if (!streamId) return;
        const res = await apiService.get<StreamResponse>(
          `/api/streams/stream/${streamId}/`
        );

        setNotesContent(res.data.stream_evaluation_summary || null);
      } catch (error) {
        console.error("Failed to fetch notes:", error);
        toast.error("Failed to fetch notes");
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [streamId]);

  /** Save or Update Notes */
  const handleSaveNotes = async (htmlContent: string) => {
    try {
      const payload = { stream_evaluation_summary: htmlContent };

      await apiService.patch(`/api/streams/stream/${streamId}/`, payload);

      setNotesContent(htmlContent);
      toast.success(
        notesContent ? "Notes updated successfully" : "Notes added successfully"
      );
      handleClose();
    } catch (error) {
      console.error("Failed to update notes:", error);
      toast.error("Failed to save notes. Please try again.");
    }
  };

  return (
    <Grid>
      <Card sx={{ boxShadow: 0, height: "auto", mt: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Event Notes
            </Typography>
            <IconButton onClick={handleOpen}>
              <AddCircleOutlineOutlined />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading notes...
              </Typography>
            ) : notesContent ? (
              <Grid size={{ xs: 12 }}>
                <div dangerouslySetInnerHTML={{ __html: notesContent }} />
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  No notes have been added yet.
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog for Editing Notes */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DialogTitle>
            {notesContent ? "Update Event Notes" : "Add Event Notes"}
          </DialogTitle>
          <Close onClick={handleClose} sx={{ mr: 3, cursor: "pointer" }} />
        </Box>

        <DialogContent>
          <RichTextEditor
            initialContent={notesContent || ""}
            onSave={handleSaveNotes}
            title="What Went Wrong / Immediate Steps"
            placeholder="Describe what went wrong and your immediate action steps here..."
            saveButtonText={notesContent ? "Update" : "Save"}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default NotesCard;
